import type { RealtimePayloads } from '@an-crm/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { startRealtime } from './connection';
import { bindRealtimeHandlers } from './handlers';

/** Socket.IO is not exercised by unit tests (no server); tests cover the handler mapping instead. */
const ENABLED = import.meta.env.MODE !== 'test';

export function useRealtime({
  onSessionRevoked,
}: {
  onSessionRevoked: (payload: RealtimePayloads['session.revoked']) => void;
}): void {
  const queryClient = useQueryClient();
  const revokedRef = useRef(onSessionRevoked);

  useEffect(() => {
    revokedRef.current = onSessionRevoked;
  }, [onSessionRevoked]);

  useEffect(() => {
    if (!ENABLED) return;
    return startRealtime((socket) =>
      bindRealtimeHandlers(socket, {
        queryClient,
        onSessionRevoked: (payload) => revokedRef.current(payload),
        onNotification: (payload) => {
          if (payload.severity === 'critical') toast.error(payload.title);
          else if (payload.severity === 'warning') toast.warning(payload.title);
        },
      }),
    );
  }, [queryClient]);
}
